import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, useAction, useApi } from "../../api.ts";
import { fmtDateTime, fmtRelative, plural } from "../../format.ts";
import {
  Badge, Button, Card, CopyButton, Dialog, EmptyState, ErrorText, Field, Input, Loading, LoadError, Mono, PageHeader,
  Pagination, Table, TBody, Td, Th, THead, Tabs, Tr, useConfirm, useToast,
} from "../../ui/index.tsx";
import { DEFAULT_DURATION, DurationField, GamePicker, downloadText, durationDays, fmtDuration, useGames, type DurationValue } from "./_common.tsx";
import type { GenerateKeysRequest, GenerateKeysResponse, LicenseKeyRow, Paged } from "../../../shared/types.ts";

type Status = "all" | "unused" | "redeemed" | "revoked";
const STATUSES: { id: Status; label: string }[] = [
  { id: "all", label: "All" },
  { id: "unused", label: "Unused" },
  { id: "redeemed", label: "Redeemed" },
  { id: "revoked", label: "Revoked" },
];

export default function Keys() {
  const [params, setParams] = useSearchParams();
  const status = (STATUSES.find((s) => s.id === params.get("status"))?.id ?? "all") as Status;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const list = useApi<Paged<LicenseKeyRow>>(`/admin/keys?status=${status}&page=${page}&limit=50`);
  const { nameOf } = useGames();
  const confirm = useConfirm();
  const toast = useToast();
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<string[] | null>(null);
  const [revoking, setRevoking] = useState<number | null>(null);

  const setQuery = (next: { status?: Status; page?: number }) => {
    const p = new URLSearchParams(params);
    if (next.status !== undefined) { next.status === "all" ? p.delete("status") : p.set("status", next.status); p.delete("page"); }
    if (next.page !== undefined) next.page > 1 ? p.set("page", String(next.page)) : p.delete("page");
    setParams(p, { replace: true });
  };

  const revoke = async (k: LicenseKeyRow) => {
    if (!(await confirm({ title: "Revoke key?", body: <>Key <Mono>{k.key_hint}</Mono> will no longer be redeemable.</>, confirmLabel: "Revoke", danger: true }))) return;
    setRevoking(k.id);
    try {
      await api.del(`/admin/keys/${k.id}`);
      toast("Key revoked", "success");
      await list.reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Revoke failed", "error");
    } finally {
      setRevoking(null);
    }
  };

  const data = list.data;

  return (
    <>
      <PageHeader title="License keys" description="Generate, track and revoke redeemable keys"
        actions={<Button variant="primary" icon="plus" onClick={() => setGenerating(true)}>Generate keys</Button>} />

      <Card padded={false}>
        <div className="px-4 pt-2">
          <Tabs tabs={STATUSES} value={status} onChange={(s) => setQuery({ status: s })} />
        </div>
        {list.error && !data ? <LoadError error={list.error} retry={list.reload} /> : !data ? <Loading /> : data.items.length === 0 ? (
          <EmptyState icon="key" title="No keys" body={status === "all" ? "Generate a batch to get started." : `No ${status} keys.`} />
        ) : (
          <>
            <Table>
              <THead>
                <tr><Th>Key</Th><Th>Games</Th><Th>Duration</Th><Th>Note</Th><Th>Created</Th><Th>Status</Th><Th>Redeemed</Th><Th /></tr>
              </THead>
              <TBody>
                {data.items.map((k) => {
                  const unused = !k.redeemed_at && !k.revoked_at;
                  return (
                    <Tr key={k.id}>
                      <Td><Mono className="text-fg">{k.key_hint}</Mono></Td>
                      <Td>
                        <div className="flex max-w-56 flex-wrap gap-1">
                          {k.games.map((g) => <Badge key={g}>{nameOf(g)}</Badge>)}
                        </div>
                      </Td>
                      <Td className="whitespace-nowrap">{fmtDuration(k.days)}</Td>
                      <Td className="max-w-48 truncate text-muted" >{k.note ?? "—"}</Td>
                      <Td className="whitespace-nowrap text-muted">
                        <div title={fmtDateTime(k.created_at)}>{fmtRelative(k.created_at)}</div>
                        {k.created_by && <div className="text-xs text-faint">{k.created_by}</div>}
                      </Td>
                      <Td>
                        {k.revoked_at ? <Badge tone="bad" dot>Revoked</Badge> : k.redeemed_at ? <Badge tone="ok" dot>Redeemed</Badge> : <Badge tone="info" dot>Unused</Badge>}
                      </Td>
                      <Td className="whitespace-nowrap text-muted">
                        {k.redeemed_at ? (
                          <>
                            {k.redeemed_by
                              ? <Link to={`/admin/users/${k.redeemed_by}`} className="text-fg hover:text-accent">{k.redeemed_by_username ?? k.redeemed_by}</Link>
                              : <span>{k.redeemed_by_username ?? "deleted user"}</span>}
                            <div className="text-xs text-faint" title={fmtDateTime(k.redeemed_at)}>{fmtRelative(k.redeemed_at)}</div>
                          </>
                        ) : "—"}
                      </Td>
                      <Td className="text-right">
                        {unused && (
                          <Button size="sm" variant="danger" loading={revoking === k.id} onClick={() => revoke(k)}>Revoke</Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
            <Pagination page={data.page} pages={data.pages} total={data.total} onChange={(p) => setQuery({ page: p })} />
          </>
        )}
      </Card>

      <GenerateDialog open={generating} onClose={() => setGenerating(false)}
        onDone={(keys) => { setGenerating(false); setGenerated(keys); void list.reload(); }} />
      <KeysDialog keys={generated} onClose={() => setGenerated(null)} />
    </>
  );
}

function GenerateDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: (keys: string[]) => void }) {
  const { games, loading } = useGames();
  const [count, setCount] = useState("10");
  const [picked, setPicked] = useState<string[]>([]);
  const [duration, setDuration] = useState<DurationValue>(DEFAULT_DURATION);
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const generate = useAction(async (body: GenerateKeysRequest) => api.post<GenerateKeysResponse>("/admin/keys", body));

  const submit = async () => {
    const n = Number(count);
    const days = durationDays(duration);
    if (!Number.isInteger(n) || n < 1 || n > 500) return setFormError("Count must be between 1 and 500.");
    if (!picked.length) return setFormError("Pick at least one game.");
    if (days === undefined) return setFormError("Enter a valid number of days, or choose lifetime.");
    setFormError(null);
    const res = await generate.run({ count: n, games: picked, days, note: note.trim() || undefined });
    if (res) {
      setCount("10"); setPicked([]); setDuration(DEFAULT_DURATION); setNote("");
      onDone(res.keys);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Generate keys" description="Keys are shown once after generation."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={generate.pending} onClick={submit}>Generate</Button>
      </>}>
      <div className="space-y-4">
        <Field label="Count" hint="1 to 500"><Input type="number" min={1} max={500} value={count} onChange={(e) => setCount(e.target.value)} className="w-28" /></Field>
        <Field label="Games"><GamePicker games={games} loading={loading} value={picked} onChange={setPicked} /></Field>
        <DurationField value={duration} onChange={setDuration} />
        <Field label="Note" hint="Optional, e.g. giveaway or reseller name"><Input value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} /></Field>
        <ErrorText>{formError ?? generate.error}</ErrorText>
      </div>
    </Dialog>
  );
}

function KeysDialog({ keys, onClose }: { keys: string[] | null; onClose: () => void }) {
  const text = keys?.join("\n") ?? "";
  return (
    <Dialog open={!!keys} onClose={onClose} wide title={keys ? `${plural(keys.length, "key")} generated` : "Keys"}
      footer={<>
        <Button icon="download" onClick={() => downloadText(`exoware-keys-${new Date().toISOString().slice(0, 10)}.txt`, text + "\n")}>Download .txt</Button>
        <Button variant="primary" onClick={onClose}>Done</Button>
      </>}>
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-sm text-warn">
          <span>Copy or download these now. Only a hint of each key is stored, so they can't be shown again.</span>
        </div>
        <div className="flex items-center justify-between text-xs text-muted">
          <span>One key per line</span>
          <span className="flex items-center gap-1">Copy all <CopyButton text={text} label="Copy all keys" /></span>
        </div>
        <pre className="max-h-80 overflow-auto rounded-lg border border-line bg-bg p-3 font-mono text-xs leading-relaxed select-all">{text}</pre>
      </div>
    </Dialog>
  );
}
